import * as THREE from 'three';

export interface VoxelHit {
  /** Integer coordinates of the hit block. */
  x: number;
  y: number;
  z: number;
  /** Unit normal of the hit face (points out of the block). */
  nx: number;
  ny: number;
  nz: number;
}

/**
 * Voxel traversal raycast (Amanatides & Woo DDA): walks the ray through
 * grid cells and returns the first solid block plus the face it entered
 * through. The cell containing the origin is skipped.
 */
export function raycastVoxels(
  isSolid: (x: number, y: number, z: number) => boolean,
  origin: THREE.Vector3,
  direction: THREE.Vector3,
  maxDistance: number,
): VoxelHit | null {
  let x = Math.floor(origin.x);
  let y = Math.floor(origin.y);
  let z = Math.floor(origin.z);

  const stepX = Math.sign(direction.x);
  const stepY = Math.sign(direction.y);
  const stepZ = Math.sign(direction.z);

  const tDeltaX = stepX === 0 ? Infinity : Math.abs(1 / direction.x);
  const tDeltaY = stepY === 0 ? Infinity : Math.abs(1 / direction.y);
  const tDeltaZ = stepZ === 0 ? Infinity : Math.abs(1 / direction.z);

  let tMaxX =
    stepX === 0
      ? Infinity
      : (stepX > 0 ? x + 1 - origin.x : origin.x - x) * tDeltaX;
  let tMaxY =
    stepY === 0
      ? Infinity
      : (stepY > 0 ? y + 1 - origin.y : origin.y - y) * tDeltaY;
  let tMaxZ =
    stepZ === 0
      ? Infinity
      : (stepZ > 0 ? z + 1 - origin.z : origin.z - z) * tDeltaZ;

  while (true) {
    let nx = 0;
    let ny = 0;
    let nz = 0;

    if (tMaxX <= tMaxY && tMaxX <= tMaxZ) {
      if (tMaxX > maxDistance) return null;
      x += stepX;
      tMaxX += tDeltaX;
      nx = -stepX;
    } else if (tMaxY <= tMaxZ) {
      if (tMaxY > maxDistance) return null;
      y += stepY;
      tMaxY += tDeltaY;
      ny = -stepY;
    } else {
      if (tMaxZ > maxDistance) return null;
      z += stepZ;
      tMaxZ += tDeltaZ;
      nz = -stepZ;
    }

    if (isSolid(x, y, z)) return { x, y, z, nx, ny, nz };
  }
}
